function getTranslateY(element) {
  const transform = element.getAttribute('transform') || ''
  const match = transform.match(/translate\s*\(\s*[\d.+-]+\s*,\s*([\d.+-]+)\s*\)/)
  return match ? parseFloat(match[1]) : 0
}

function getAxisFromContainer(axisContainer, axis) {
  if (!axisContainer) return null
  const children = Array.from(axisContainer.children || []).filter(
    (node) => node && node.nodeType === 1
  )
  if (children.length === 0) return null
  // Single child can only be the x-axis (at largest translateY); no y-axis present
  if (children.length === 1) return axis === 'x' ? children[0] : null

  const translations = children.map((child) => getTranslateY(child))
  const hasDistinctTranslations = new Set(translations).size > 1

  // X-axis is placed at chart bottom — largest positive y-translation.
  // Y-axis is placed at chart left — minimal (near-zero) y-translation.
  // Some SVG producers omit those transforms, however. In that case preserve
  // RAWGraphs' x-then-y child order rather than resolving both axes to the
  // first child.
  if (!hasDistinctTranslations) {
    return axis === 'x' ? children[0] : children[children.length - 1]
  }

  if (axis === 'x') {
    return children.reduce((best, child) =>
      getTranslateY(child) > getTranslateY(best) ? child : best
    )
  }
  if (axis === 'y') {
    return children.reduce((best, child) =>
      getTranslateY(child) < getTranslateY(best) ? child : best
    )
  }
  return null
}

export function getAxisGroupNode(svgNode, axis = 'x') {
  if (!svgNode) return null

  // Direct ID lookup: #xAxis / #yAxis
  const normalizedAxis = axis === 'y' ? 'yAxis' : 'xAxis'
  const directAxis = svgNode.querySelector(`#${normalizedAxis}`)
  if (directAxis) return directAxis

  // Spaced-ID fallback: boxplot/treemap use "#x axis" / "#y axis"
  const spacedAxis = axis === 'y' ? 'y axis' : 'x axis'
  const spacedDirectAxis = svgNode.querySelector(`[id="${spacedAxis}"]`)
  if (spacedDirectAxis) return spacedDirectAxis

  // Generic #axis container: beeswarm, bubblechart, contourplot,
  // hexagonalbinning, horizongraph, linechart, voronoidiagram, etc.
  const axisContainer = svgNode.querySelector('#axis')
  if (axisContainer) {
    return getAxisFromContainer(axisContainer, axis)
  }

  return null
}

// Returns ALL matching axis groups — needed for small-multiples charts where
// each panel has its own axis group (e.g. multiset barchart with duplicate
// #xAxis IDs, or linechart with a #axis container per series panel).
export function getAllAxisGroupNodes(svgNode, axis = 'x') {
  if (!svgNode) return []

  const normalizedId = axis === 'y' ? 'yAxis' : 'xAxis'
  const spacedId = axis === 'y' ? 'y axis' : 'x axis'

  const byId = Array.from(svgNode.querySelectorAll(`#${normalizedId}`))
  if (byId.length > 0) return byId

  const bySpaced = Array.from(svgNode.querySelectorAll(`[id="${spacedId}"]`))
  if (bySpaced.length > 0) return bySpaced

  // Multiple #axis containers: linechart with series creates one per panel
  const axisContainers = Array.from(svgNode.querySelectorAll('#axis'))
  if (axisContainers.length > 0) {
    return axisContainers
      .map((container) => getAxisFromContainer(container, axis))
      .filter(Boolean)
  }

  return []
}
